<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
    import SparrIcon from "@/assets/integritetssparr.svg";
    import SarskildaLakemedelIcon from "@/assets/kontrollerat.svg";
    import SamtyckeIcon from "@/assets/samtycker.svg";
    import SamtyckeSaknasIcon from "@/assets/samtyckerEJ.svg";
    import RedCrossIcon from "@/assets/status_struken.svg";
    import UnlockedSparrIcon from "@/assets/unlock_user_red.svg";
    import KButton from "@/components/Button.vue";
    import KTag from "@/components/KTag.vue";
    import { useStore } from "@/store";
    import { SessionData } from "@/store/modules/recept/nllModule";
    import DialogAktivPatient from "@/tabs/Operation/DialogAktivPatient.vue";
    import { hasNLLReadPermission } from "@/tabs/Receptforskrivning/helpers";
    import {
        BasePatientInfo,
        isNLLPatientInfo,
        NLL_CONSENT_TYPES,
        NLLPatientInfo,
        NllSamtycke,
    } from "@/tabs/Receptforskrivning/types";
    import VisaSparradeForskrivningar from "@/tabs/Receptforskrivning/VisaSparradeForskrivningar.vue";
    import { computed, ref, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        patientinfo: NLLPatientInfo | BasePatientInfo;
    }>();

    const { patientinfo } = toRefs(props);

    const profile = computed(() => store.getters["userData/currentProfile"]);

    const showVisaSparrade = ref(false);

    const sessionData = computed<SessionData>(() => store.getters["nll/sessionData"]);

    const getConsentDisplay = ({ samtycke }: { samtycke: string }) => {
        if (!samtycke) {
            return null;
        }

        return NLL_CONSENT_TYPES?.get(samtycke)?.display;
    };

    const toggleShowVisaSparrade = () => {
        showVisaSparrade.value = !showVisaSparrade.value;
    };

    const isIntegritetssamtycke = ({ samtycke }: { samtycke: string | null }) => {
        if (!samtycke) {
            return false;
        }

        return NllSamtycke.TillfalligtSparradeUppgifter === samtycke;
    };

    const isFormodatsamtycke = ({ samtycke }: { samtycke: string | null }) => {
        if (!samtycke) {
            return false;
        }

        return NllSamtycke.Formodat === samtycke;
    };

    const isNodatkomstsamtycke = ({ samtycke }: { samtycke: string | null }) => {
        if (!samtycke) {
            return false;
        }

        return NllSamtycke.Nodatkomst === samtycke;
    };
</script>

<template>
    <div class="patientinfo">
        <DialogAktivPatient
            :patient="patientinfo.patient"
            :showHeader="false"
            :showBirthDate="!isNLLPatientInfo(patientinfo)"
            size="small"
        />
        <div v-if="hasNLLReadPermission(profile) && sessionData?.accessToken" class="tags">
            <template v-if="isNLLPatientInfo(patientinfo)">
                <KTag v-if="patientinfo.samtycke === null">
                    <template #icon> <img :src="SamtyckeSaknasIcon" /> </template>
                    <span>Samtycke saknas</span>
                </KTag>
                <KTag v-else-if="patientinfo.samtycke">
                    <template #icon> <img :src="SamtyckeIcon" /> </template>
                    <span>{{ getConsentDisplay({ samtycke: patientinfo.samtycke }) }}</span>
                </KTag>
            </template>
            <template v-if="isNLLPatientInfo(patientinfo)">
                <KTag v-if="patientinfo.patient.folkbokford === 'NO'">
                    <template #icon> <img :src="RedCrossIcon" /> </template>
                    <span>Patient ej folkbokförd</span>
                </KTag>
                <KTag v-if="patientinfo.patient.folkbokford === 'UNKNOWN'">
                    <template #icon> <img :src="RedCrossIcon" /> </template>
                    <span>Patientens folkbokföring okänd</span>
                </KTag>
                <KTag v-if="patientinfo.sarskilda_lakemedel_finns">
                    <template #icon> <img :src="SarskildaLakemedelIcon" /> </template>
                    <span>Särskilda läkemedel de senaste 24 månaderna</span>
                </KTag>
                <template v-if="patientinfo.sparrar_finns">
                    <template v-if="isFormodatsamtycke({ samtycke: patientinfo.samtycke }) && !patientinfo.dospatient">
                        <KTag>
                            <template #icon>
                                <img :src="SparrIcon" alt="Spärrade förskrivningar och uttag finns" />
                            </template>
                            <span>Spärrade förskrivningar och uttag</span>
                        </KTag>
                    </template>
                    <template
                        v-else-if="
                            !isIntegritetssamtycke({ samtycke: patientinfo.samtycke }) &&
                            !isNodatkomstsamtycke({ samtycke: patientinfo.samtycke }) &&
                            !patientinfo.dospatient
                        "
                    >
                        <KButton type="button" @click="toggleShowVisaSparrade" size="small" variant="tag">
                            <img :src="SparrIcon" alt="Spärr" />
                            <span>Spärrade förskrivningar och uttag</span>
                        </KButton>
                    </template>
                    <template v-else>
                        <KTag>
                            <template #icon
                                ><img :src="UnlockedSparrIcon" alt="Visar spärrade förskrivningar och uttag" />
                            </template>
                            <span>Visar spärrade förskrivningar och uttag</span>
                        </KTag>
                    </template>
                </template>
            </template>
            <template v-else>
                <KTag>
                    <template #icon> <img :src="RedCrossIcon" /> </template>
                    <span> Patienten finns inte i NLL, information från Kliniken visas här </span>
                </KTag>
            </template>
        </div>
        <VisaSparradeForskrivningar
            v-if="showVisaSparrade"
            @cancel="toggleShowVisaSparrade"
            @submit="toggleShowVisaSparrade"
        />
    </div>
</template>

<style lang="scss">
    .patientinfo {
        display: flex;
        align-items: center;
        gap: 37px;

        .tags {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
        }
    }
</style>
